import React, { useState, useEffect } from 'react';
import { repasseDocumentoService, RepasseDocumentoResumo } from '../../services/repasseDocumentoService';
import { apoliceService } from '../../services/apoliceService';
import type { ApoliceData } from '../../services/apoliceService';
import { ApoliceFormModal } from '../apolice/ApoliceFormModal';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { useNotifier } from '../ui/Notifier';

interface RepassePorDocumentoTableProps {
  refreshKey?: number;
}

export const RepassePorDocumentoTable: React.FC<RepassePorDocumentoTableProps> = ({ refreshKey }) => {
  const [documentos, setDocumentos] = useState<RepasseDocumentoResumo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState<string>('dataEmissao');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 10;

  // Modal states
  const [showApoliceModal, setShowApoliceModal] = useState(false);
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | null>(null);
  const notifier = useNotifier();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadDocumentos = async () => {
    setLoading(true);
    try {
      const response = await repasseDocumentoService.getResumoRepasses({
        page: currentPage,
        limit: itemsPerPage,
        sortBy: sortField,
        sortOrder: sortOrder,
        search: debouncedSearchTerm || undefined
      });

      setDocumentos(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      console.error('Erro ao carregar documentos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocumentos();
  }, [currentPage, sortField, sortOrder, debouncedSearchTerm, refreshKey]);

  const handleOpenApolice = async (documento: RepasseDocumentoResumo) => {
    try {
      console.log('Buscando apólice com ID:', documento.id);
      const apolice = await apoliceService.getById(documento.id);
      console.log('Apólice retornada:', apolice);
      setSelectedApolice((apolice && (apolice as any).data) ? (apolice as any).data : (apolice as any));
      setShowApoliceModal(true);
    } catch (err: any) {
      console.error('Erro ao carregar apólice:', err);
      notifier.show({ message: err.message || 'Erro ao carregar apólice', severity: 'error' });
    }
  };

  const handleCloseApoliceModal = () => {
    setShowApoliceModal(false);
    setSelectedApolice(null);
  };

  const handleNextApoliceRequested = async (currentId?: string) => {
    try {
      if (!documentos || documentos.length === 0) return undefined;
      const idx = documentos.findIndex((d) => String(d.id) === String(currentId));
      const nextIdx = idx >= 0 && idx < documentos.length - 1 ? idx + 1 : (idx === -1 ? 0 : idx);
      const nextDoc = documentos[nextIdx];
      if (!nextDoc) return undefined;
      const apolice = await apoliceService.getById(nextDoc.id);
      const apoliceData = (apolice && (apolice as any).data) ? (apolice as any).data : (apolice as any);
      setSelectedApolice(apoliceData);
      return apoliceData;
    } catch (err: any) {
      console.error('Erro ao carregar próxima apólice:', err);
      return undefined;
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header com Busca */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xs font-semibold" style={{ color: '#3d2b56' }}>
            Repasses por Documento
          </h2>
          <p className="text-xs mt-1" style={{ color: '#9E9E9E' }}>
            Resumo de repasses agrupados por documento
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <select
              value={sortField}
              onChange={(e) => { setSortField(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
              style={{ borderColor: '#C3B4DE', backgroundColor: '#FFFFFF', color: '#121212', minWidth: '140px' }}
            >
              <option value="dataPagamento">Data Pagamento</option>
              <option value="cliente">Cliente</option>
              <option value="numeroDocumento">Proposta</option>
              <option value="repassePago">Valor Pago</option>
              <option value="valorComissao">Comissão</option>
              <option value="percentualRepasse">% Repasse</option>
            </select>

            <button
              onClick={() => { setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'); setCurrentPage(1); }}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <input
            type="text"
            placeholder="Buscar repasses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', minWidth: '200px' }}
          />

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
          >
            Filtros {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">Carregando...</p>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && documentos.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {documentos.map((doc) => {
            const percent = doc.resumoRepasses?.percentualRepassado;
            const borderColor = (percent !== undefined && percent !== null)
              ? (Number(percent) >= 100 ? '#0B7A3A' : '#8B1E3F')
              : '#7A61B2';

            return (
              <div
                key={doc.id}
                className="bg-white rounded-lg shadow-sm border-l-2 p-3 hover:shadow-md transition-shadow"
                style={{ borderLeftColor: borderColor }}
              >
              {/* Header com Ações (moved to Produto row) */}

              {/* Conteúdo do Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                {/* Cliente */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{doc.cliente?.nome || '-'}</div>
                  </div>
                </div>

                {/* Seguradora */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Seguradora</div>
                    <div className="text-sm font-medium text-gray-900 truncate">{doc.seguradora?.nome || '-'}</div>
                  </div>
                </div>

                {/* Produto + Ação */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <div>
                      <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produto</div>
                      <div className="text-sm font-medium text-gray-900 truncate">{doc.produto?.nome || '-'}</div>
                    </div>
                  </div>

                  <div className="ml-2 flex-shrink-0">
                    <button
                      onClick={() => handleOpenApolice(doc)}
                      className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                      title="Editar Apólice"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2v6h6" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11h8M8 15h8M8 7h3" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Proposta */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Proposta</div>
                    <div className="text-sm font-medium text-gray-900">{doc.codigoProposta || '-'}</div>
                  </div>
                </div>

                {/* Apólice */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Apólice</div>
                    <div className="text-sm font-medium text-gray-900">{doc.numeroApolice || '-'}</div>
                  </div>
                </div>

                {/* Vigência */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Vigência</div>
                    <div className="text-sm font-medium text-gray-900">
                      {doc.inicioVigencia && doc.fimVigencia 
                        ? `${formatDate(doc.inicioVigencia)} - ${formatDate(doc.fimVigencia)}`
                        : '-'}
                    </div>
                  </div>
                </div>

                {/* Comissão Documento */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Comissão Doc.</div>
                    <div className="text-sm font-medium text-gray-900">{doc.comissao ? formatCurrency(Number(doc.comissao)) : '-'}</div>
                  </div>
                </div>

                {/* Total Repasses */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#0B7A3A' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Total Repasses</div>
                    <div className="text-sm font-semibold" style={{ color: '#0B7A3A' }}>
                      {doc.resumoRepasses?.total ? formatCurrency(Number(doc.resumoRepasses.total)) : '-'}
                    </div>
                  </div>
                </div>

                {/* Quantidade Repasses */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Qtd. Repasses</div>
                    <div className="text-sm font-medium text-gray-900">{doc.resumoRepasses?.quantidade || 0}</div>
                  </div>
                </div>

                {/* Diferença */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: borderColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Diferença</div>
                    <div className="text-sm font-medium" style={{ color: borderColor }}>
                      {doc.resumoRepasses?.diferenca ? formatCurrency(Number(doc.resumoRepasses.diferenca)) : '-'}
                    </div>
                  </div>
                </div>

                {/* % Repassado */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>% Repassado</div>
                    <div className="text-sm font-medium text-gray-900">
                      {doc.resumoRepasses?.percentualRepassado !== undefined 
                        ? `${Number(doc.resumoRepasses.percentualRepassado).toFixed(1)}%` 
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && documentos.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">
            {searchTerm ? 'Nenhum documento encontrado.' : 'Nenhum documento com repasses.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {documentos.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-xs text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
              Página {currentPage} de {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal de Apólice */}
      {showApoliceModal && selectedApolice && (
        <ApoliceFormModal
          apolice={selectedApolice}
          isOpen={showApoliceModal}
          isEditing={true}
          onClose={handleCloseApoliceModal}
          onSave={(updatedApolice) => {
            handleCloseApoliceModal();
            loadDocumentos();
          }}
          onUpdated={(updatedApolice) => {
            loadDocumentos();
          }}
          onNextRequested={handleNextApoliceRequested}
        />
      )}
    </div>
  );
};
